<script setup lang="ts">
import { localizePlace } from '@/data/regions'
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { RouterLink } from 'vue-router'
import { ArrowRight, Gavel, ShieldCheck, CheckCircle2, MapPin, Zap, Camera, Radio } from 'lucide-vue-next'
import type { Auction } from '@/types'
import { useFormatters } from '@/composables/useFormatters'
import { useI18n } from '@/composables/useI18n'
import { useUserStore } from '@/stores/user'
import HeroScene from '@/components/home/HeroScene.vue'

interface Props {
  featured: Auction | null
  liveList: Auction[]
  stats: { active: number; bids: number; sellers: number }
}

const props = defineProps<Props>()
const emit = defineEmits<{ openBid: [auction: Auction] }>()

const { currency } = useFormatters()
const { formatMoney } = currency
const { t, currentLang } = useI18n()
const userStore = useUserStore()

const isOwnListing = computed(() => {
  if (!userStore.user || !props.featured) return false
  return props.featured.sellerId === userStore.user.id
})

// One shared ticker drives the featured countdown and the side list.
const now = ref(Date.now())
let timer: ReturnType<typeof setInterval> | null = null
onMounted(() => { timer = setInterval(() => { now.value = Date.now() }, 1000) })
onUnmounted(() => { if (timer) clearInterval(timer) })

function split(endsAt: string) {
  const total = Math.max(0, Math.floor((new Date(endsAt).getTime() - now.value) / 1000))
  const d = Math.floor(total / 86400)
  const h = Math.floor((total % 86400) / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return { total, d, h, m, s }
}

const countdown = computed(() => {
  if (!props.featured?.endsAt) return null
  const c = split(props.featured.endsAt)
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    ended: c.total <= 0,
    urgent: c.total > 0 && c.total < 3600,
    parts: c.d > 0
      ? [
          { v: String(c.d), l: t('landing.unitDay') },
          { v: pad(c.h), l: t('landing.unitHour') },
          { v: pad(c.m), l: t('landing.unitMin') },
        ]
      : [
          { v: pad(c.h), l: t('landing.unitHour') },
          { v: pad(c.m), l: t('landing.unitMin') },
          { v: pad(c.s), l: t('landing.unitSec') || t('lotPage.unitSec') },
        ],
  }
})

function shortLeft(endsAt: string) {
  const c = split(endsAt)
  if (c.total <= 0) return t('auction.auctionEnded')
  if (c.d > 0) return `${c.d}${t('landing.unitDay')} ${c.h}${t('landing.unitHour')}`
  if (c.h > 0) return `${c.h}${t('landing.unitHour')} ${String(c.m).padStart(2, '0')}${t('landing.unitMin')}`
  return `${c.m}${t('landing.unitMin')} ${String(c.s).padStart(2, '0')}${t('landing.unitSec') || t('lotPage.unitSec')}`
}

function isUrgent(endsAt: string) {
  const c = split(endsAt)
  return c.total > 0 && c.total < 3600
}

const nextBid = computed(() => {
  if (!props.featured) return null
  const cur = props.featured.currentPrice.minorUnits
  const step = props.featured.bidIncrement?.minorUnits || 0
  const minor = cur + step
  return { ...props.featured.currentPrice, minorUnits: minor, amount: (minor / 100).toFixed(2), formatted: '' }
})

const fmt = (n: number) => n.toLocaleString('ru-RU')

// The headline rises word by word; words, not letters, so it works for any
// language and wraps normally.
const titleWords = computed(() => t('landing.heroTitle').split(/\s+/).filter(Boolean))

// Platform numbers count up from zero the first time they arrive.
const shown = ref({ active: 0, bids: 0, sellers: 0 })
let raf = 0
function countUp(target: { active: number; bids: number; sellers: number }) {
  cancelAnimationFrame(raf)
  const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  if (reduce) { shown.value = { ...target }; return }
  const from = { ...shown.value }
  const start = performance.now()
  const DURATION = 1100
  const step = (now: number) => {
    const p = Math.min(1, (now - start) / DURATION)
    const e = 1 - Math.pow(1 - p, 3)
    shown.value = {
      active: Math.round(from.active + (target.active - from.active) * e),
      bids: Math.round(from.bids + (target.bids - from.bids) * e),
      sellers: Math.round(from.sellers + (target.sellers - from.sellers) * e),
    }
    if (p < 1) raf = requestAnimationFrame(step)
  }
  raf = requestAnimationFrame(step)
}
watch(() => props.stats, v => countUp(v), { immediate: true, deep: true })
onUnmounted(() => cancelAnimationFrame(raf))

// Nothing live yet (a brand-new platform, or a quiet night): instead of an
// empty featured slot, an empty ticker and a row of zeros, the hero becomes
// the pitch — headline on the left, how a deal works on the right.
const visibleStats = computed(() => [
  { id: 'active', label: t('landing.statActive'), value: shown.value.active, real: props.stats.active },
  { id: 'bids', label: t('landing.statBids'), value: shown.value.bids, real: props.stats.bids },
  { id: 'sellers', label: t('landing.statSellers'), value: shown.value.sellers, real: props.stats.sellers },
].filter(s => s.real > 0))

const isQuiet = computed(() => !props.featured && props.liveList.length === 0)
const quietSteps = computed(() => [
  { icon: Camera, title: t('landing.quietStep1Title'), desc: t('landing.quietStep1Desc') },
  { icon: Radio, title: t('landing.quietStep2Title'), desc: t('landing.quietStep2Desc') },
  { icon: ShieldCheck, title: t('landing.quietStep3Title'), desc: t('landing.quietStep3Desc') },
])
</script>

<template>
  <section class="relative overflow-hidden rounded-[1.5rem] sm:rounded-[2rem] bg-ink text-white">
    <HeroScene />

    <!-- Quiet state: pitch + how a deal works -->
    <div v-if="isQuiet" class="relative grid grid-cols-1 items-center gap-8 p-6 sm:p-10 lg:grid-cols-12 lg:gap-12 lg:p-14">
      <div class="space-y-5 lg:col-span-7">
        <div class="inline-flex max-w-[calc(100%-4rem)] items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/80 sm:max-w-none">
          <span class="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary live-dot" />
          <span>{{ t('landing.eyebrow') }}</span>
        </div>
        <h1 class="max-w-2xl font-display text-[2rem] leading-[1.06] sm:text-5xl lg:text-[3.4rem] font-bold tracking-tight text-white text-balance">
          <template v-for="(w, i) in titleWords" :key="i"><span class="hero-word" :style="{ animationDelay: `${120 + i * 70}ms` }">{{ w }}</span>{{ ' ' }}</template>
        </h1>
        <p class="hero-rise max-w-xl text-sm sm:text-base leading-relaxed text-white/75" style="animation-delay: 420ms">
          {{ t('landing.heroSubtitle') }}
        </p>
        <div class="hero-rise flex flex-wrap gap-2.5 pt-1" style="animation-delay: 520ms">
          <RouterLink
            to="/sell"
            class="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-bold text-onPrimary shadow-[0_8px_24px_-8px_rgb(var(--color-primary)/0.6)] transition hover:bg-primary-hover active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-ink"
          >
            <span>{{ t('landing.sellCta') }}</span>
            <ArrowRight class="h-4 w-4" />
          </RouterLink>
          <RouterLink
            to="/how-it-works"
            class="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/[0.06] px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-ink"
          >
            {{ t('nav.howItWorks') }}
          </RouterLink>
        </div>
        <p class="text-xs text-white/60">{{ t('landing.quietNote') }}</p>
      </div>

      <div class="lg:col-span-5">
        <div class="hero-rise rounded-card border border-white/10 bg-white/[0.06] p-5 backdrop-blur-md sm:p-6" style="animation-delay: 620ms">
          <p class="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/55">{{ t('landing.quietTitle') }}</p>
          <ol class="mt-4 space-y-3">
            <li v-for="(step, i) in quietSteps" :key="i" class="flex gap-3.5 rounded-xl bg-white/[0.04] p-3.5">
              <span class="relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-onPrimary">
                <component :is="step.icon" class="h-5 w-5" />
                <span class="absolute -right-1.5 -top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-ink text-[11px] font-bold tabular text-primaryOnDark ring-2 ring-ink">{{ i + 1 }}</span>
              </span>
              <div class="min-w-0">
                <p class="font-display text-[15px] font-semibold text-white">{{ step.title }}</p>
                <p class="mt-0.5 text-[13px] leading-relaxed text-white/60">{{ step.desc }}</p>
              </div>
            </li>
          </ol>
        </div>
      </div>
    </div>

    <div v-else class="relative grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 p-5 sm:p-8 lg:p-10">

      <!-- Headline column -->
      <div class="lg:col-span-4 flex flex-col justify-between gap-6">
        <div class="space-y-4">
          <div class="inline-flex max-w-[calc(100%-4rem)] items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/80 sm:max-w-none">
            <span class="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary live-dot" />
            <span>{{ t('landing.eyebrow') }}</span>
          </div>
          <h1 class="font-display text-[1.75rem] leading-[1.08] sm:text-4xl lg:text-[2.6rem] font-bold tracking-tight text-white">
            <template v-for="(w, i) in titleWords" :key="i"><span class="hero-word" :style="{ animationDelay: `${120 + i * 70}ms` }">{{ w }}</span>{{ ' ' }}</template>
          </h1>
          <p class="hero-rise max-w-md text-sm sm:text-[15px] leading-relaxed text-white/75" style="animation-delay: 420ms">
            {{ t('landing.heroSubtitle') }}
          </p>
          <div class="hero-rise flex flex-wrap gap-2.5 pt-1" style="animation-delay: 520ms">
            <RouterLink
              to="/auctions"
              class="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-bold text-onPrimary shadow-[0_8px_24px_-8px_rgb(var(--color-primary)/0.6)] transition hover:bg-primary-hover active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-ink"
            >
              <span>{{ t('landing.ctaBrowse') }}</span>
              <ArrowRight class="h-4 w-4" />
            </RouterLink>
            <RouterLink
              to="/sell"
              class="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/[0.06] px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-ink"
            >
              {{ t('landing.ctaSell') }}
            </RouterLink>
          </div>
        </div>

        <!-- Real platform numbers. A zero is worse than silence — "0 bids, 0
             verified sellers" on the front page tells a first visitor the
             place is empty — so only the counters that have something to say
             are rendered. -->
        <dl v-if="visibleStats.length" class="grid gap-3 border-t border-white/10 pt-5" :class="visibleStats.length === 1 ? 'grid-cols-1' : visibleStats.length === 2 ? 'grid-cols-2' : 'grid-cols-3'">
          <div v-for="stat in visibleStats" :key="stat.id">
            <dt class="text-[11px] uppercase tracking-wider text-white/60">{{ stat.label }}</dt>
            <dd class="font-display text-xl sm:text-2xl font-bold tabular text-white">{{ fmt(stat.value) }}</dd>
          </div>
        </dl>
      </div>

      <!-- Featured lot card -->
      <div class="lg:col-span-5">
        <div v-if="featured" class="hero-rise flex h-full flex-col overflow-hidden rounded-card bg-surface text-text-primary shadow-[0_24px_60px_-24px_rgba(0,0,0,0.6)]" style="animation-delay: 560ms">
          <RouterLink :to="`/auctions/${featured.id}`" class="group relative block aspect-[16/10] overflow-hidden bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary">
            <img
              :src="featured.images?.[0] || '/placeholder-lot.svg'"
              :alt="featured.title"
              class="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
            />
            <div class="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/60 to-transparent" />
            <div class="absolute left-3 top-3 flex items-center gap-2">
              <span class="inline-flex items-center gap-1.5 rounded-full bg-surface/95 px-2.5 py-1 text-[11px] font-semibold text-text-primary shadow-sm">
                <span class="relative inline-flex h-1.5 w-1.5 rounded-full bg-success text-success live-dot" />
                {{ t('landing.liveNow') }}
              </span>
              <span class="rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur">
                {{ t('landing.featuredLot') }}
              </span>
            </div>
            <div class="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-3">
              <h2 class="font-display text-base sm:text-lg font-semibold leading-snug text-white line-clamp-2 drop-shadow">
                {{ featured.title }}
              </h2>
              <span class="shrink-0 inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-1 text-[11px] font-semibold text-white backdrop-blur">
                <MapPin class="h-3 w-3" />{{ localizePlace(featured.city, currentLang) }}
              </span>
            </div>
          </RouterLink>

          <div class="flex flex-1 flex-col gap-4 p-4 sm:p-5">
            <div class="flex flex-wrap items-start gap-x-4 gap-y-3">
              <div class="min-w-0">
                <div class="text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">{{ t('home.currentBid') }}</div>
                <div class="mt-1 font-display text-2xl sm:text-[1.75rem] font-bold tabular leading-none text-text-primary">
                  {{ formatMoney(featured.currentPrice) }}
                </div>
                <div class="mt-1.5 inline-flex items-center gap-1 text-xs text-text-secondary">
                  <Gavel class="h-3.5 w-3.5" />
                  <span>{{ t('home.bidsCount', { n: featured.bidCount }) }}</span>
                </div>
              </div>
              <div v-if="countdown" class="ml-auto shrink-0 text-right">
                <div class="text-[11px] font-semibold uppercase tracking-[0.14em]" :class="countdown.urgent ? 'text-live' : 'text-text-muted'">
                  {{ t('landing.endsIn') }}
                </div>
                <div class="mt-1 flex justify-end gap-1.5">
                  <div v-for="(p, i) in countdown.parts" :key="i" class="min-w-[2.6rem] rounded-lg px-1.5 py-1 text-center" :class="countdown.urgent ? 'bg-live/10 text-live' : 'bg-accent text-text-primary'">
                    <div class="font-mono text-lg font-bold leading-none tabular">{{ p.v }}</div>
                    <div v-if="p.l" class="mt-0.5 text-[11px] font-medium uppercase leading-none text-text-muted">{{ p.l }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="featured.seller" class="flex items-center gap-2.5 rounded-xl bg-accent/70 px-3 py-2">
              <img v-if="featured.seller.avatar" :src="featured.seller.avatar" :alt="featured.seller.fullName" class="h-8 w-8 rounded-full object-cover" />
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-1 text-xs font-semibold text-text-primary">
                  <span class="truncate">{{ featured.seller.fullName }}</span>
                  <CheckCircle2 v-if="featured.seller.kycStatus === 'verified'" class="h-3.5 w-3.5 shrink-0 text-success" />
                </div>
                <div class="text-[11px] text-text-muted">{{ featured.seller.kycStatus === 'verified' ? t('landing.verifiedSeller') : localizePlace(featured.city, currentLang) }}</div>
              </div>
              <span class="inline-flex items-center gap-1 text-[11px] font-medium text-success"><ShieldCheck class="h-3.5 w-3.5" />Escrow</span>
            </div>

            <div class="mt-auto grid gap-2" :class="featured.buyNowPrice ? 'grid-cols-1 sm:grid-cols-[1fr_auto] lg:grid-cols-1 xl:grid-cols-[1fr_auto]' : 'grid-cols-1'">
              <button
                v-if="!isOwnListing"
                type="button"
                class="inline-flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 rounded-xl bg-primary px-3 py-3 text-sm font-bold text-onPrimary transition hover:bg-primary-hover active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface sm:px-4"
                @click="emit('openBid', featured)"
              >
                <span class="inline-flex items-center gap-1.5">
                  <Gavel class="h-4 w-4 shrink-0" />
                  <span>{{ t('home.placeBid') }}</span>
                </span>
                <span v-if="nextBid" class="rounded-md bg-ink/10 px-1 py-0.5 font-mono text-[11px] font-semibold tabular">{{ formatMoney(nextBid) }}</span>
              </button>
              <RouterLink
                v-else
                :to="`/auctions/${featured.id}`"
                class="inline-flex items-center justify-center rounded-xl border border-border bg-surface px-4 py-3 text-sm font-semibold text-text-primary transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
              >
                {{ t('home.lotDetails') }}
              </RouterLink>
              <RouterLink
                v-if="featured.buyNowPrice"
                :to="`/auctions/${featured.id}`"
                class="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border px-3.5 py-3 text-xs font-semibold text-text-primary transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
                :title="t('landing.buyNow')"
              >
                <Zap class="h-3.5 w-3.5 text-warning" />
                <span class="tabular">{{ formatMoney(featured.buyNowPrice) }}</span>
              </RouterLink>
            </div>
          </div>
        </div>

        <div v-else class="flex h-full min-h-[240px] flex-col items-center justify-center rounded-card border border-dashed border-white/15 p-8 text-center">
          <p class="text-sm text-white/60">{{ t('landing.noLots') }}</p>
          <RouterLink to="/sell" class="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-onPrimary transition hover:bg-primary-hover active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-ink">
            {{ t('landing.sellCta') }}<ArrowRight class="h-4 w-4" />
          </RouterLink>
        </div>
      </div>

      <!-- Ending-soon ticker list -->
      <div class="lg:col-span-3 flex flex-col">
        <div class="mb-3 flex items-center justify-between">
          <div class="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/60">
            <span class="relative inline-flex h-1.5 w-1.5 rounded-full bg-success text-success live-dot" />
            {{ t('landing.liveNow') }}
          </div>
          <RouterLink
            to="/auctions?sort=ending_soon"
            class="-my-2 -mr-2.5 inline-flex min-h-[40px] items-center rounded-xl px-2.5 py-2 text-[11px] font-semibold text-primaryOnDark transition hover:bg-white/10 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-ink"
          >
            {{ t('common.viewAll') }}
          </RouterLink>
        </div>
        <ul v-if="liveList.length" class="flex flex-col gap-2">
          <li v-for="lot in liveList" :key="lot.id">
            <RouterLink
              :to="`/auctions/${lot.id}`"
              class="group flex items-center gap-3 rounded-xl border border-white/[0.07] bg-white/[0.045] p-2 transition hover:border-white/15 hover:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-ink"
            >
              <img :src="lot.images?.[0] || '/placeholder-lot.svg'" :alt="lot.title" class="h-12 w-14 shrink-0 rounded-lg object-cover" loading="lazy" />
              <div class="min-w-0 flex-1">
                <p class="truncate text-[13px] font-medium text-white group-hover:text-primaryOnDark">{{ lot.title }}</p>
                <div class="mt-0.5 flex items-center gap-2 text-[11px]">
                  <span class="font-display font-semibold tabular text-white/90">{{ formatMoney(lot.currentPrice) }}</span>
                  <span class="text-white/35">·</span>
                  <span class="tabular" :class="isUrgent(lot.endsAt) ? 'text-live font-semibold' : 'text-white/55'">{{ shortLeft(lot.endsAt) }}</span>
                </div>
              </div>
              <span class="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-white/80"><Gavel class="h-3 w-3" />{{ lot.bidCount }}</span>
            </RouterLink>
          </li>
        </ul>
        <div v-else class="rounded-xl border border-dashed border-white/15 p-6 text-center text-xs text-white/50">
          {{ t('landing.noLots') }}
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Headline words and the blocks under them rise into place once, on load.
   Reduced motion: everything is simply there. */
.hero-word {
  display: inline-block;
}
@media (prefers-reduced-motion: no-preference) {
  .hero-word {
    animation: hero-word-in 0.7s cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  .hero-rise {
    animation: hero-rise-in 0.8s cubic-bezier(0.16, 1, 0.3, 1) both;
  }
}
@keyframes hero-word-in {
  from { opacity: 0; transform: translate3d(0, 0.6em, 0); filter: blur(6px); }
  to { opacity: 1; transform: none; filter: none; }
}
@keyframes hero-rise-in {
  from { opacity: 0; transform: translate3d(0, 1rem, 0); }
  to { opacity: 1; transform: none; }
}

/* Hold the entrance while the splash curtain is still down. */
:global(.is-splashing) .hero-word,
:global(.is-splashing) .hero-rise {
  animation-play-state: paused;
}
</style>
